import axios from 'axios';

// Axios インスタンス設定（main.tsxで設定されたbaseURLを使用）
const apiClient = axios.create({
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// エラーハンドリング用のインターセプター
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Sponsor API エラー:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export interface SponsorSettings {
  companyId: number;
  isSponsor: boolean;
  updatedAt: string;
  logoUrl?: string;
}

export interface UpdateSponsorSettingsRequest {
  isSponsor: boolean;
}

export interface SponsorCompany {
  id: number;
  companyName: string;
  businessType?: string;
  logoUrl?: string;
}

export interface CompanyWithSponsorSettings {
  id: number;
  companyName: string;
  isSponsor: boolean;
  status: string;
  businessType?: string;
  updatedAt: string;
  logoUrl?: string;
}

export interface SponsorSettingsResponse {
  success: true;
  data: SponsorSettings;
}

export interface SponsorCompaniesResponse {
  success: true;
  data: {
    companies: SponsorCompany[];
    total: number;
  };
}

export interface AllCompaniesWithSponsorResponse {
  success: true;
  data: {
    companies: CompanyWithSponsorSettings[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export class SponsorService {
  /**
   * スポンサー設定を更新
   */
  async updateSponsorSettings(
    companyId: number,
    settings: UpdateSponsorSettingsRequest
  ): Promise<SponsorSettingsResponse> {
    const response = await apiClient.put(
      `/api/sponsor/settings/${companyId}`,
      settings
    );
    return response.data;
  }

  /**
   * スポンサー設定を取得
   */
  async getSponsorSettings(
    companyId: number
  ): Promise<SponsorSettingsResponse> {
    const response = await apiClient.get(`/api/sponsor/settings/${companyId}`);
    return response.data;
  }

  /**
   * スポンサー企業一覧を取得（学生向け）
   */
  async getSponsorCompanies(): Promise<SponsorCompaniesResponse> {
    const response = await apiClient.get('/api/sponsor/companies');
    return response.data;
  }

  /**
   * 全企業のスポンサー設定一覧を取得（管理者専用）
   */
  async getAllCompaniesWithSponsorSettings(
    page: number = 1,
    limit: number = 20,
    search?: string
  ): Promise<AllCompaniesWithSponsorResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) {
      params.append('search', search);
    }

    const response = await apiClient.get(
      `/api/sponsor/admin/companies?${params.toString()}`
    );
    return response.data;
  }
}

// デフォルトエクスポート
export const sponsorService = new SponsorService();
